import { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  GitBranch,
  Globe,
  LogOut,
  Sparkles,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Language_Options } from '@gokrew/ui/constants/language-options';
import { TranslationNS } from '@/constants/translation-namespaces';
import styles from './MobileUserMenu.module.css';

type View = 'root' | 'language' | 'branch';

export interface MobileUserMenuOption {
  code: string;
  label: string;
}

export interface MobileUserMenuProps {
  initials: string;
  name?: string;
  subtitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerClassName: string;
  avatarClassName: string;
  chevronClassName: string;
  currentLang: string;
  onLanguageSelect: (code: string) => void;
  branches: MobileUserMenuOption[];
  activeBranchCode: string;
  showBranches: boolean;
  onBranchSelect: (code: string) => void;
  onProfile: () => void;
  onReleaseNotes: () => void;
  onLogout: () => void;
}

const MobileUserMenu = ({
  initials,
  name,
  subtitle,
  open,
  onOpenChange,
  triggerClassName,
  avatarClassName,
  chevronClassName,
  currentLang,
  onLanguageSelect,
  branches,
  activeBranchCode,
  showBranches,
  onBranchSelect,
  onProfile,
  onReleaseNotes,
  onLogout,
}: MobileUserMenuProps) => {
  const { t } = useTranslation(TranslationNS.Dashboard);
  const [view, setView] = useState<View>('root');

  const handleOpenChange = (next: boolean) => {
    if (!next) setView('root');
    onOpenChange(next);
  };

  const activeBranch = branches.find(
    (branch) => branch.code === activeBranchCode
  );

  const options = view === 'language' ? Language_Options : branches;
  // Language matches on the base tag ('en-US' selects 'en'); branch ids
  // are exact.
  const isActiveOption = (code: string) =>
    view === 'language'
      ? currentLang.split('-')[0] === code
      : activeBranchCode === code;
  const onOptionSelect =
    view === 'language' ? onLanguageSelect : onBranchSelect;

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={triggerClassName}
          aria-label={t('header.user_menu')}
        >
          <div className={avatarClassName}>
            <span>{initials}</span>
          </div>
          <ChevronDown size={18} className={chevronClassName} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" sideOffset={10} className={styles.panel}>
        {view === 'root' ? (
          <>
            <div className={styles.identity}>
              <div className={`${avatarClassName} ${styles.identityAvatar}`}>
                <span>{initials}</span>
              </div>
              <div className={styles.identityText}>
                <span className={styles.identityName}>{name}</span>
                <span className={styles.identityRole}>{subtitle}</span>
              </div>
            </div>

            <div className={styles.sectionLabel}>
              {t('header.section_account')}
            </div>

            <DropdownMenuItem className={styles.row} onSelect={onProfile}>
              <User size={18} className={styles.rowIcon} />
              <span className={styles.rowLabel}>{t('header.profile')}</span>
              <ChevronRight size={16} className={styles.rowChevron} />
            </DropdownMenuItem>

            <DropdownMenuItem
              className={styles.row}
              onSelect={(event) => {
                event.preventDefault();
                setView('language');
              }}
            >
              <Globe size={18} className={styles.rowIcon} />
              <span className={styles.rowLabel}>{t('header.languages')}</span>
              <span className={styles.rowValue}>
                {currentLang.toUpperCase()}
              </span>
              <ChevronRight size={16} className={styles.rowChevron} />
            </DropdownMenuItem>

            <div className={styles.sectionLabel}>
              {t('header.section_workspace')}
            </div>

            {showBranches && (
              <DropdownMenuItem
                className={styles.row}
                onSelect={(event) => {
                  event.preventDefault();
                  setView('branch');
                }}
              >
                <GitBranch size={18} className={styles.rowIcon} />
                <span className={styles.rowLabel}>{t('header.branch')}</span>
                <span className={styles.rowValue}>{activeBranch?.label}</span>
                <ChevronRight size={16} className={styles.rowChevron} />
              </DropdownMenuItem>
            )}

            <DropdownMenuItem className={styles.row} onSelect={onReleaseNotes}>
              <Sparkles size={18} className={styles.rowIcon} />
              <span className={styles.rowLabel}>{t('header.whats_new')}</span>
              <span className={styles.badge}>{t('header.badge_new')}</span>
            </DropdownMenuItem>

            <div className={styles.divider} />

            <DropdownMenuItem
              className={`${styles.row} ${styles.rowDanger}`}
              onSelect={onLogout}
            >
              <LogOut size={18} className={styles.rowIcon} />
              <span className={styles.rowLabel}>{t('header.logout')}</span>
            </DropdownMenuItem>
          </>
        ) : (
          <>
            <DropdownMenuItem
              className={`${styles.row} ${styles.backRow}`}
              onSelect={(event) => {
                event.preventDefault();
                setView('root');
              }}
            >
              <ChevronLeft size={16} className={styles.rowIcon} />
              <span className={styles.rowLabel}>
                {view === 'language'
                  ? t('header.languages')
                  : t('header.branch')}
              </span>
            </DropdownMenuItem>

            {options.map(({ code, label }) => (
              <DropdownMenuItem
                key={code}
                className={styles.row}
                onSelect={() => onOptionSelect(code)}
              >
                <span className={styles.rowLabel}>{label}</span>
                {isActiveOption(code) && (
                  <Check size={16} className={styles.rowCheck} />
                )}
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default MobileUserMenu;
